/**
 * Workflow DAG — Core data structure for multi-step drug discovery workflows.
 *
 * A Workflow is a directed acyclic graph (DAG) where:
 * - Nodes are ActionDef instances with parameter overrides
 * - Edges encode data flow (which output feeds which input)
 * - Conditional edges support branching
 *
 * @module gibbs/workflow/Workflow
 */

import { getAction } from '../registry/index';

// ─── Execution State Constants ──────────────────────────────────────────────

/** @enum {string} */
export const ExecutionStatus = Object.freeze({
    PENDING: 'PENDING',
    RUNNING: 'RUNNING',
    SUCCEEDED: 'SUCCEEDED',
    FAILED: 'FAILED',
    SKIPPED: 'SKIPPED',
    CANCELLED: 'CANCELLED',
});

// ─── Data Classes ────────────────────────────────────────────────────────────

let nodeIdCounter = 0;
let edgeIdCounter = 0;

/**
 * @typedef {Object} WorkflowNode
 * @property {string} id - Auto-generated unique node ID (e.g., "node_1")
 * @property {string} actionId - ActionDef ID from registry (e.g., "energy-minimize")
 * @property {string} label - Human-readable label (defaults to ActionDef name)
 * @property {Object} paramOverrides - Parameter values overriding ActionDef defaults
 * @property {{ x: number, y: number }} position - Optional UI position hint
 * @property {Object|undefined} executionState - Transient runtime
 *   execution state (not serialized)
 */

/**
 * Create a WorkflowNode.
 *
 * @param {string} actionId
 * @param {Object} [options]
 * @param {string} [options.label]
 * @param {Object} [options.paramOverrides]
 * @param {{ x: number, y: number }} [options.position]
 * @returns {WorkflowNode}
 */
export function createNode(actionId, options = {}) {
    const actionDef = getAction(actionId);
    return {
        id: `node_${++nodeIdCounter}`,
        actionId,
        label: options.label || (actionDef ? actionDef.name : actionId),
        paramOverrides: options.paramOverrides || {},
        position: options.position || { x: 0, y: 0 },
    };
}

/**
 * @typedef {Object} WorkflowEdge
 * @property {string} id - Auto-generated unique edge ID (e.g., "edge_1")
 * @property {string} sourceNodeId - ID of the upstream node
 * @property {string} targetNodeId - ID of the downstream node
 * @property {string} outputName - Name of the output from the source ActionDef
 * @property {string} inputName - Name of the input on the target ActionDef
 * @property {Object|null} condition - Optional condition for conditional branching
 */

/**
 * Create a WorkflowEdge.
 *
 * @param {string} sourceNodeId
 * @param {string} targetNodeId
 * @param {string} outputName
 * @param {string} inputName
 * @param {Object} [options]
 * @param {Object} [options.condition] - Condition object for branching
 * @returns {WorkflowEdge}
 */
export function createEdge(sourceNodeId, targetNodeId, outputName, inputName, options = {}) {
    return {
        id: `edge_${++edgeIdCounter}`,
        sourceNodeId,
        targetNodeId,
        outputName,
        inputName,
        condition: options.condition || null,
    };
}

// ─── Workflow Class ──────────────────────────────────────────────────────────

export class Workflow {
    constructor() {
        /** @type {Map<string, WorkflowNode>} */
        this.nodes = new Map();
        /** @type {Map<string, WorkflowEdge>} */
        this.edges = new Map();
        /** @type {Map<string, Set<string>>} Outgoing edge IDs per node */
        this.outgoing = new Map();
        /** @type {Map<string, Set<string>>} Incoming edge IDs per node */
        this.incoming = new Map();
    }

    /**
     * Add a node to the workflow.
     *
     * @param {string} actionId - ActionDef ID from registry
     * @param {Object} [options] - Node options (label, paramOverrides, position)
     * @returns {WorkflowNode} The created node
     * @throws {Error} If actionId is not found in registry
     */
    addNode(actionId, options = {}) {
        const actionDef = getAction(actionId);
        if (!actionDef) {
            throw new Error(`Unknown actionId: "${actionId}". Not found in registry.`);
        }

        const node = createNode(actionId, options);
        this.nodes.set(node.id, node);
        this.outgoing.set(node.id, new Set());
        this.incoming.set(node.id, new Set());
        return node;
    }

    /**
     * Add an edge (data flow connection) between two nodes.
     *
     * @param {string} sourceNodeId
     * @param {string} targetNodeId
     * @param {string} outputName - Output from source ActionDef
     * @param {string} inputName - Input on target ActionDef
     * @param {Object} [options] - Edge options (condition)
     * @returns {WorkflowEdge} The created edge
     * @throws {Error} If source or target node doesn't exist
     */
    addEdge(sourceNodeId, targetNodeId, outputName, inputName, options = {}) {
        if (!this.nodes.has(sourceNodeId)) {
            throw new Error(`Source node not found: "${sourceNodeId}"`);
        }
        if (!this.nodes.has(targetNodeId)) {
            throw new Error(`Target node not found: "${targetNodeId}"`);
        }

        // Check for duplicate edge
        for (const edgeId of this.outgoing.get(sourceNodeId)) {
            const existing = this.edges.get(edgeId);
            if (existing.targetNodeId === targetNodeId
                && existing.outputName === outputName
                && existing.inputName === inputName) {
                return existing; // Duplicate, return existing
            }
        }

        const edge = createEdge(sourceNodeId, targetNodeId, outputName, inputName, options);
        this.edges.set(edge.id, edge);
        this.outgoing.get(sourceNodeId).add(edge.id);
        this.incoming.get(targetNodeId).add(edge.id);
        return edge;
    }

    /**
     * Remove a node and all its connected edges.
     *
     * @param {string} nodeId
     * @returns {boolean} True if node existed and was removed
     */
    removeNode(nodeId) {
        if (!this.nodes.has(nodeId)) return false;

        // Remove all connected edges
        const outgoing = this.outgoing.get(nodeId) || new Set();
        const incoming = this.incoming.get(nodeId) || new Set();

        for (const edgeId of [...outgoing, ...incoming]) {
            this.removeEdge(edgeId);
        }

        this.nodes.delete(nodeId);
        this.outgoing.delete(nodeId);
        this.incoming.delete(nodeId);
        return true;
    }

    /**
     * Remove an edge.
     *
     * @param {string} edgeId
     * @returns {boolean} True if edge existed and was removed
     */
    removeEdge(edgeId) {
        const edge = this.edges.get(edgeId);
        if (!edge) return false;

        this.outgoing.get(edge.sourceNodeId)?.delete(edgeId);
        this.incoming.get(edge.targetNodeId)?.delete(edgeId);
        this.edges.delete(edgeId);
        return true;
    }

    // ─── Query Methods ───────────────────────────────────────────────────

    getNodes() { return Array.from(this.nodes.values()); }
    getEdges() { return Array.from(this.edges.values()); }
    getNode(id) { return this.nodes.get(id); }
    getEdge(id) { return this.edges.get(id); }

    getOutgoingEdges(nodeId) {
        const ids = this.outgoing.get(nodeId);
        return ids ? Array.from(ids).map((id) => this.edges.get(id)).filter(Boolean) : [];
    }

    getIncomingEdges(nodeId) {
        const ids = this.incoming.get(nodeId);
        return ids ? Array.from(ids).map((id) => this.edges.get(id)).filter(Boolean) : [];
    }

    /** Nodes with no incoming edges (workflow entry points). */
    getRootNodes() {
        return this.getNodes().filter((n) => {
            const incoming = this.incoming.get(n.id);
            return !incoming || incoming.size === 0;
        });
    }

    /** Nodes with no outgoing edges (workflow endpoints). */
    getLeafNodes() {
        return this.getNodes().filter((n) => {
            const outgoing = this.outgoing.get(n.id);
            return !outgoing || outgoing.size === 0;
        });
    }

    /** Number of nodes. */
    get nodeCount() { return this.nodes.size; }

    /** Number of edges. */
    get edgeCount() { return this.edges.size; }

    // ─── Execution State Methods ────────────────────────────────────────

    /**
     * Set the transient execution state for a node.
     *
     * @param {string} nodeId
     * @param {Object} state - Execution state update
     * @throws {Error} If nodeId does not exist
     */
    setExecutionState(nodeId, state) {
        const node = this.nodes.get(nodeId);
        if (!node) {
            throw new Error(`Cannot set execution state: node "${nodeId}" not found`);
        }
        node.executionState = {
            status: state.status,
            startTime: state.startTime ?? null,
            endTime: state.endTime ?? null,
            error: state.error ?? null,
            resultSummary: state.resultSummary ?? null,
        };
    }

    /**
     * Get the transient execution state for a node.
     *
     * @param {string} nodeId
     * @returns {Object|undefined} Execution state object
     */
    getExecutionState(nodeId) {
        const node = this.nodes.get(nodeId);
        return node ? node.executionState : undefined;
    }
}

/**
 * Reset the ID counters (for testing).
 */
export function resetIdCounters() {
    nodeIdCounter = 0;
    edgeIdCounter = 0;
}
